import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

const NAV = [
  { label: 'Inicio', path: '/' },
  {
    label: 'Nosotros',
    path: '/nosotros',
    children: [
      { label: 'Quiénes Somos', path: '/nosotros/quienes-somos' },
      { label: 'Misión y Visión', path: '/nosotros/mision-vision' },
      { label: 'Valores Corporativos', path: '/nosotros/valores' },
    ],
  },
  {
    label: 'Servicios',
    path: '/servicios',
    children: [
      {
        label: 'Minería',
        path: '/servicios/mineria',
        subchildren: [
          { label: 'Planes de Ventilación', path: '/servicios/ventilacion' },
          { label: 'Planes de Sostenimiento', path: '/servicios/sostenimiento' },
          { label: 'Prevención Metano y Polvo', path: '/servicios/metano-polvo' },
          { label: 'PTO – Plan de Trabajos y Obras', path: '/servicios/pto' },
          { label: 'Asesorías Mineras', path: '/servicios/asesorias-mineras' },
        ],
      },
      {
        label: 'Ambiental',
        path: '/servicios/ambiental',
        subchildren: [
          { label: 'ICAS', path: '/servicios/icas' },
          { label: 'Estudios de Aguas', path: '/servicios/estudios-aguas' },
          { label: 'Licencias Ambientales', path: '/servicios/licencias' },
          { label: 'PMA – Plan de Manejo Ambiental', path: '/servicios/pma' },
          { label: 'Huella de Carbono', path: '/servicios/huella-carbono' },
        ],
      },
      {
        label: 'Software',
        path: '/software',
        subchildren: [
          { label: 'Software Minero',    path: '/software/minero' },
          { label: 'Software Ambiental', path: '/software/ambiental' },
        ],
      },
    ],
  },
  { label: 'Contacto', path: '/contacto' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [mobileExpand, setMobileExpand] = useState(null);
  const [mobileSubExp, setMobileSubExp] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);
  // Timers para delay en cierre — evita que el gap dispare onMouseLeave
  const closeTimer = useRef(null);
  const closeSubTimer = useRef(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setOpenSub(null);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null); setOpenSub(null); setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(path);

  /* ── Handlers con delay para evitar cierre prematuro ── */
  const handleMenuEnter = (label) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };

  const handleMenuLeave = () => {
    closeTimer.current = setTimeout(() => {
      setOpenMenu(null);
      setOpenSub(null);
    }, 120);
  };

  const handleSubEnter = (label) => {
    clearTimeout(closeTimer.current);
    clearTimeout(closeSubTimer.current);
    setOpenSub(label);
  };

  const handleSubLeave = () => {
    closeSubTimer.current = setTimeout(() => setOpenSub(null), 120);
  };

  return (
    <header className={`nav${scrolled ? ' nav--scrolled' : ''}`} ref={navRef}>
      <div className="nav__inner">

        {/* Logo */}
        <Link to="/" className="nav__logo">
          <picture>
            <source srcSet="/logo3.webp" type="image/webp" />
            <img src="/logo3.webp" alt="Impacta M.A. SAS" className="nav__logo-img" />
          </picture>
        </Link>

        {/* Desktop links */}
        <nav className="nav__links" aria-label="Menú principal">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="nav__item"
              onMouseEnter={() => item.children && handleMenuEnter(item.label)}
              onMouseLeave={handleMenuLeave}
            >
              {item.children ? (
                <button
                  type="button"
                  className={`nav__link${isActive(item.path) ? ' nav__link--active' : ''} nav__link--has-arrow`}
                  onClick={() => setOpenMenu(openMenu === item.label ? null : item.label)}
                >
                  {item.label}
                  <svg className={`nav__chevron${openMenu === item.label ? ' nav__chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ) : (
                <Link
                  to={item.path}
                  className={`nav__link${isActive(item.path) ? ' nav__link--active' : ''}`}
                >
                  {item.label}
                </Link>
              )}

              {/* Dropdown L1 */}
              {item.children && openMenu === item.label && (
                <div
                  className="nav__dropdown"
                  onMouseEnter={() => clearTimeout(closeTimer.current)}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="nav__dropdown-box" role="menu">
                    {item.children.map((child) => (
                      <div
                        key={child.label}
                        className="nav__dropdown-row"
                        onMouseEnter={() => child.subchildren && handleSubEnter(child.label)}
                        onMouseLeave={() => child.subchildren && handleSubLeave()}
                      >
                        <Link to={child.path} className="nav__dropdown-link" role="menuitem">
                          <span>{child.label}</span>
                          {child.subchildren && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </Link>

                        {/* Dropdown L2 */}
                        {child.subchildren && openSub === child.label && (
                          <div
                            className="nav__subdropdown"
                            onMouseEnter={() => { clearTimeout(closeTimer.current); clearTimeout(closeSubTimer.current); }}
                            onMouseLeave={handleMenuLeave}
                          >
                            <div className="nav__subdropdown-box" role="menu">
                              {child.subchildren.map((sub) => (
                                <Link key={sub.label} to={sub.path} className="nav__dropdown-link nav__dropdown-link--sub" role="menuitem">
                                  {sub.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>{/* nav__dropdown-box */}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* CTA */}
        <Link to="/contacto" className="nav__cta btn btn--blue btn--sm">
          Solicitar Cotización
        </Link>

        {/* Hamburger */}
        <button
          className={`nav__burger${mobileOpen ? ' nav__burger--open' : ''}`}
          onClick={() => setMobileOpen(v => !v)}
          aria-label="Abrir menú"
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="nav__mobile" role="dialog" aria-label="Menú móvil">
          {NAV.map((item) => (
            <div key={item.label} className="nav__mob-item">
              <div className="nav__mob-row" onClick={() => setMobileExpand(mobileExpand === item.label ? null : item.label)}>
                <Link to={item.path} className="nav__mob-link">{item.label}</Link>
                {item.children && (
                  <svg className={`nav__mob-chevron${mobileExpand === item.label ? ' open' : ''}`} width="14" height="14" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              {item.children && mobileExpand === item.label && (
                <div className="nav__mob-dropdown">
                  {item.children.map((child) => (
                    <div key={child.label}>
                      <div className="nav__mob-row nav__mob-row--l2" onClick={() => setMobileSubExp(mobileSubExp === child.label ? null : child.label)}>
                        <Link to={child.path} className="nav__mob-link nav__mob-link--l2">{child.label}</Link>
                        {child.subchildren && (
                          <svg className={`nav__mob-chevron${mobileSubExp === child.label ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      {child.subchildren && mobileSubExp === child.label && (
                        <div className="nav__mob-dropdown--l3">
                          {child.subchildren.map((sub) => (
                            <Link key={sub.label} to={sub.path} className="nav__mob-link nav__mob-link--l3">{sub.label}</Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="nav__mob-footer">
            <Link to="/contacto" className="btn btn--blue" style={{ width: '100%', justifyContent: 'center' }}>
              Solicitar Cotización
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
